import React from 'react';
import { ChevronRight, ChevronDown, BookOpen, GraduationCap, Calendar, FileText, GripVertical, FolderOpen } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../ui/popover';
import type { InstructionalRollupKind } from '../../utils/topicInstructionalUnits';
import { formatBackendUnitStateLabel } from '../../utils/backendUnitStateLabel';
import { formatMediumOrdinalDate } from '../../utils/formatMediumOrdinalDate';

export interface TreeNode {
  id: string;
  label: string;
  type: 'subject' | 'keystage' | 'year' | 'topic' | 'topicGroup';
  children?: TreeNode[];
  icon?: React.ComponentType<{ size?: number; className?: string }>;
  data?: any;
}

export interface TreeNodeRendererProps {
  node: TreeNode;
  depth: number;
  isExpanded: boolean;
  isLoading: boolean;
  showExpandIcon: boolean;
  showCheckbox: boolean;
  isChecked: boolean;
  isIndeterminate: boolean;
  checkboxDisabled: boolean;
  isDisabled: boolean;
  isSelected?: boolean; // Whether this node is selected in the tree
  isTopicSelectedInCards: boolean;
  /** Rollup over instructional units (topic leaf only); undefined skips instructional row accents. */
  topicLeafInstructionalRollup?: InstructionalRollupKind;
  instructionalUnits?: any[];
  onOpenInstructionalUnit?: (unit: any) => void;
  assessmentScore: number | null;
  passFailThreshold: number | null;
  hasChildTopics: boolean;
  childTopicsCount: number;
  yearTopicsCount?: number; // For year nodes
  parentTopicNames?: string[];
  dragHandleProps?: any; // Props for drag handle from react-beautiful-dnd
  previousParentTopicNames?: string[]; // Previous topic's parent topic names for truncation/color logic
  previousPrimaryTag?: string | null; // Previous topic's primary tag for color alternation
  currentColorIndex?: number; // Current color index for alternating colors
  onToggleExpand: () => void;
  onCheckboxClick: (e: React.MouseEvent) => void;
  onNodeClick: () => void;
}

export function TreeNodeRenderer({
  node,
  depth,
  isExpanded,
  isLoading,
  showExpandIcon,
  showCheckbox,
  isChecked,
  isIndeterminate,
  checkboxDisabled,
  isDisabled,
  isSelected,
  isTopicSelectedInCards,
  topicLeafInstructionalRollup,
  instructionalUnits,
  onOpenInstructionalUnit,
  assessmentScore,
  passFailThreshold,
  hasChildTopics,
  childTopicsCount,
  yearTopicsCount,
  parentTopicNames,
  dragHandleProps,
  previousParentTopicNames,
  previousPrimaryTag,
  currentColorIndex = 0,
  onToggleExpand,
  onCheckboxClick,
  onNodeClick
}: TreeNodeRendererProps) {
  const Icon = node.type === 'topicGroup' ? FolderOpen : (node.icon || BookOpen);
  const showTopicGroupChrome = node.type === 'topicGroup';
  const [unitsPopoverOpen, setUnitsPopoverOpen] = React.useState(false);

  const instructionalUnitsSortedByCreatedDesc = React.useMemo(() => {
    const list = instructionalUnits ?? [];
    if (!list.length) return list;
    return [...list].sort((a: any, b: any) => {
      const tb = new Date(b?.e251_CreatedAt ?? 0).getTime();
      const ta = new Date(a?.e251_CreatedAt ?? 0).getTime();
      const nb = Number.isNaN(tb) ? Number.NEGATIVE_INFINITY : tb;
      const na = Number.isNaN(ta) ? Number.NEGATIVE_INFINITY : ta;
      return nb - na;
    });
  }, [instructionalUnits]);

  const hasTags = parentTopicNames && Array.isArray(parentTopicNames) && parentTopicNames.length > 0;
  
  // Color classes for alternating badges
  const colorClasses = [
    'bg-slate-100 text-slate-400',
    'bg-stone-100 text-stone-400'
  ];
  
  // Determine current color index based on primary tag change
  let activeColorIndex = currentColorIndex;
  if (hasTags && parentTopicNames.length > 0) {
    const primaryTag = parentTopicNames[0]?.trim();
    if (previousPrimaryTag !== null && previousPrimaryTag !== primaryTag) {
      // Primary tag changed, alternate color
      activeColorIndex = (currentColorIndex + 1) % colorClasses.length;
    }
  }

  // Helper function to truncate text to first word + "..."
  // If the first word is an article/determiner ("The", "A", "My"), include both first and second word
  const truncateToFirstWord = (text: string): string => {
    if (!text) return text;
    const words = text.trim().split(/\s+/);
    if (words.length <= 1) return text;
    
    // Check if first word is an article/determiner
    const articles = ['The', 'A', 'My'];
    const firstWord = words[0];
    const isArticle = articles.includes(firstWord);
    
    if (isArticle && words.length > 1) {
      // Include both the article and the second word
      return words[0] + ' ' + words[1] + '...';
    } else {
      // Use just the first word
      return words[0] + '...';
    }
  };

  const rollup = topicLeafInstructionalRollup;
  const rowComplete = rollup === 'allComplete';
  const rowActive = rollup === 'active';

  return (
    <div key={node.id} className={hasTags ? 'mb-2 pb-2 border-b border-gray-100' : 'mb-1'}>
      <div
        className={`flex items-center py-1 px-2 rounded ${
          isDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
        } ${
          isTopicSelectedInCards ? 'bg-action-blue/15 text-action-blue-dark' : 'text-gray-700'
        } ${
          rowComplete ? 'bg-confirm-action/10 border-l-4 border-confirm-action' : ''
        } ${
          rowActive ? 'bg-orange-50 border-l-4 border-orange-500' : ''
        }`}
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
        onClick={onNodeClick}
      >
        {showCheckbox && (
          <input
            type="checkbox"
            ref={(el) => {
              if (el) {
                el.indeterminate = isIndeterminate;
              }
            }}
            checked={isChecked}
            disabled={checkboxDisabled}
            onChange={onCheckboxClick}
            onClick={(e) => e.stopPropagation()}
            className="mr-2 w-4 h-4 text-action-blue border-gray-300 rounded focus:ring-action-blue disabled:opacity-50 disabled:cursor-not-allowed"
          />
        )}
        {showExpandIcon && (
          <div className="mr-1">
            {isLoading ? (
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-500" />
            ) : isExpanded ? (
              <ChevronDown size={14} className="text-gray-500" />
            ) : (
              <ChevronRight size={14} className="text-gray-500" />
            )}
          </div>
        )}
        {!showExpandIcon && <div className="w-1 mr-1" />}
        
        {/* Segment drag handle for virtual group folders at year level */}
        {node.type === 'topicGroup' && dragHandleProps && (
          <div
            {...dragHandleProps}
            className="mr-2 flex items-center justify-center cursor-grab active:cursor-grabbing bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-md"
            title="Drag to reorder group"
            style={{
              width: '24px',
              height: '24px',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <GripVertical size={18} className="text-gray-600" strokeWidth={2} />
          </div>
        )}

        {/* Drag handle - show for draggable topic nodes (when dragHandleProps is provided) */}
        {/* Show even when checkboxes are visible - dragging and selection can coexist */}
        {node.type === 'topic' && dragHandleProps && (
          <div 
            {...dragHandleProps} 
            className="mr-2 flex items-center justify-center cursor-grab active:cursor-grabbing bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-md"
            title="Drag to reorder"
            style={{ 
              width: '24px', 
              height: '24px', 
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <GripVertical size={18} className="text-gray-600" strokeWidth={2} />
          </div>
        )}
        
        {node.type !== 'topic' && node.type !== 'topicGroup' && <Icon size={16} className="mr-2 text-gray-600" />}
        {showTopicGroupChrome && <Icon size={16} className="mr-2 text-gray-600" />}
        <span className="text-sm font-medium inline-flex flex-wrap items-center gap-1">
          {(node.type !== 'topicGroup' || showTopicGroupChrome) && <span>{node.label}</span>}
          {instructionalUnits && instructionalUnits.length > 0 && (
            <Popover open={unitsPopoverOpen} onOpenChange={setUnitsPopoverOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  tabIndex={0}
                  aria-haspopup="dialog"
                  aria-expanded={unitsPopoverOpen}
                  aria-label={`Show ${instructionalUnits.length} instructional unit${instructionalUnits.length === 1 ? '' : 's'}, opens panel`}
                  title="Click to view units"
                  className={`ml-2 inline-flex max-w-[13rem] cursor-pointer shrink-0 items-center gap-0.5 truncate rounded-md border px-1.5 py-0.5 text-left text-xs font-medium shadow-sm transition-[box-shadow,background-color,border-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-action-blue active:scale-[0.98] ${
                    rowComplete
                      ? 'border-confirm-action/50 bg-confirm-action/15 text-confirm-action-dark hover:border-confirm-action hover:bg-confirm-action/25 active:bg-confirm-action/15'
                      : 'border-orange-400/80 bg-orange-100 text-orange-900 hover:border-orange-500 hover:bg-orange-200 active:bg-orange-100'
                  }`}
                  onClick={e => e.stopPropagation()}
                  onKeyDown={e => e.stopPropagation()}
                >
                  <span className="min-w-0 truncate">
                    {instructionalUnits.length} unit
                    {instructionalUnits.length === 1 ? '' : 's'}
                  </span>
                  <ChevronDown
                    size={13}
                    className={`shrink-0 opacity-70 transition-transform duration-150 ${unitsPopoverOpen ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-[min(100vw-3rem,24rem)] p-3"
                side="bottom"
                onOpenAutoFocus={e => e.preventDefault()}
              >
                <p className="mb-2 text-xs font-semibold text-gray-700">Instructional units for this topic</p>
                <div className="max-h-[min(60vh,16rem)] overflow-auto rounded border border-gray-200">
                  <table className="w-full text-left text-xs">
                    <thead className="sticky top-0 bg-gray-50 text-[11px] uppercase tracking-wide text-gray-600">
                      <tr>
                        <th className="px-2 py-1.5 font-medium">Status</th>
                        <th className="px-2 py-1.5 font-medium">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {instructionalUnitsSortedByCreatedDesc.map((u: any) => {
                        const uid = u?.e251_UnitId as string | undefined;
                        const canNavigate =
                          !!onOpenInstructionalUnit && typeof uid === 'string' && uid.length > 0;
                        const stateLabel = formatBackendUnitStateLabel(u?.e251_State);
                        const createdText = formatMediumOrdinalDate(u?.e251_CreatedAt);
                        const rowKey = uid ?? `${stateLabel}-${createdText}-${u?.e251_CreatedAt ?? ''}`;
                        return (
                          <tr key={rowKey} className="border-t border-gray-100">
                            <td className="max-w-[10rem] px-2 py-1.5">
                              {canNavigate ? (
                                <button
                                  type="button"
                                  className="w-full truncate text-left text-action-blue-dark underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-blue rounded"
                                  onClick={() => {
                                    onOpenInstructionalUnit(u);
                                    setUnitsPopoverOpen(false);
                                  }}
                                >
                                  {stateLabel}
                                </button>
                              ) : (
                                <span className="block truncate text-gray-700">{stateLabel}</span>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-2 py-1.5 text-gray-700 tabular-nums">
                              {createdText || '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </PopoverContent>
            </Popover>
          )}
          {rollup === 'allComplete' && assessmentScore != null && (
            <span
              className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                assessmentScore < (passFailThreshold ?? 50)
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-confirm-action/15 text-confirm-action-dark'
              }`}
            >
              Score: {assessmentScore}%
            </span>
          )}
          {node.type === 'year' && yearTopicsCount != null && yearTopicsCount > 0 && (
            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-action-blue/15 text-action-blue-dark">
              {yearTopicsCount} topics
            </span>
          )}
          {showTopicGroupChrome && childTopicsCount > 0 && (
            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-500">
              {childTopicsCount} topics
            </span>
          )}
        </span>
      </div>
      {/* Display parent topic names as badges - outside flex container so they appear below */}
      {hasTags && (
        <div 
          className="flex flex-wrap gap-1" 
          style={{ 
            marginLeft: `${depth * 20 + 8 + 24}px`, 
            marginTop: '4px'
          }}
        >
          {parentTopicNames.map((parentName: string, index: number) => {
            const trimmedName = parentName?.trim() || parentName;
            
            // Determine color class - use current color index for all badges in this topic
            const colorClass = colorClasses[activeColorIndex];
            
            // Determine if this badge should be truncated
            // Truncation only applies within a year - resets when starting a new year
            // Truncate if the badge at the same index position in the previous topic (within the same year) has the same text
            // This ensures the first occurrence of a label value in a row shows full text,
            // and subsequent occurrences show truncated text until the label changes
            const shouldTruncate = previousParentTopicNames && 
                                   previousParentTopicNames.length > index &&
                                   previousParentTopicNames[index]?.trim() === trimmedName;
            
            const displayText = shouldTruncate ? truncateToFirstWord(parentName) : parentName;
            
            return (
              <span
                key={index}
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}
              >
                {displayText}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
